import { ChangeEvent, FC, useEffect, useState } from "react";
import { useCreateReswareCredentialMutation, useGetReswareCredentialByIdQuery, useUpdateReswareCredentialMutation } from "../integrationsApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetAllStatesQuery } from "../../../data/states/statesApi";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import { OptionType } from "../../../../helpers/utils/StyleUtils";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import Modal from "../../../../components/common/form/Modal";
import Form from "../../../../components/common/form/Form";
import InputField from "../../../../components/common/form/InputField";
import SelectField from "../../../../components/common/form/SelectField";
import { createOptionListForSelectTag } from "../../../../helpers/utils/Utils";
import Button from "../../../../components/common/form/Button";

interface ReswareCredentialsFormProps {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    customerId?: string;
    credentialId?: string;
    isReadOnly?: boolean;
}

const ReswareCredentialsForm: FC<ReswareCredentialsFormProps> = ({ show, setShow, customerId, credentialId }) => {
    const [credentialsData, setCredentialsData] = useState<any>({});

    const [updateCredentialApi, updateCredentialResponse] = useUpdateReswareCredentialMutation();
    const [createCredentialApi, createCredentialResponse] = useCreateReswareCredentialMutation();
    const { data: credentialDetails } = useGetReswareCredentialByIdQuery(
        customerId && credentialId ? { customerId, credentialId } : skipToken
    );
    const { data: enums } = useGetEnumsQuery("");

    useEffect(() => {
        if (credentialDetails) {
            setCredentialsData({
                ...credentialDetails,
                productType: credentialDetails.productType
                    ? enums?.["ProductType"]
                        ?.filter((el: any) => el.constant === credentialDetails.productType)
                        ?.map((el: any) => ({
                            value: credentialDetails.productType,
                            label: el.value
                        }))[0] // assuming you want a single object, not an array
                    : null
            });
        }
    }, [credentialDetails]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;
        setCredentialsData({
            ...credentialsData,
            [name]: value
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Build the payload with type-safe structure
        const credentialPayload = {
            ...credentialsData,
            customerId,
            productType: credentialsData?.productType?.value || null
        };

        // Choose between create and update API
        const apiCall = credentialId
            ? () => updateCredentialApi({ id: credentialId, data: credentialPayload }).unwrap()
            : () => createCredentialApi({ id: customerId, data: credentialPayload }).unwrap();

        try {
            await handleRTKQuery(apiCall, (response:any) => {
                if (response.success) {
                    setShow(false);
                    setCredentialsData({});
                }
            });
        } catch (error) {
            console.error("Credential submit failed", error);
        }
    };



    return (
        <Modal
            title={credentialId ? "Update Resware Credentials" : "Add Resware Credentials"}
            show={show}
            setShow={setShow}
            className="w-2/5 bg-text-0 rounded-lg shadow-lg"
            fields={
                <Form onSubmit={handleSubmit} fields={
                    <div className="text-sm">
                        <InputField
                            label="Username"
                            name="username"
                            value={credentialsData?.username || ""}
                            type="text"
                            onChange={handleInputChange}
                            required
                        />

                        <InputField
                            label="Password"
                            name="password"
                            value={credentialsData?.password || ""}
                            type="text"
                            onChange={handleInputChange}
                            required={true}
                        />

                        <InputField
                            label="Url"
                            name="url"
                            value={credentialsData?.url || ""}
                            type="text"
                            onChange={handleInputChange}
                            className="mb-2"
                            required={true}
                        />
                        <SelectField
                            placeholder='Select Product Type...'
                            value={credentialsData?.productType || null}
                            options={createOptionListForSelectTag(enums?.["ProductType"], "value", "constant")}
                            onChange={(selectedOption) => handleInputChange({ name: "productType", value: selectedOption })}
                            label="Product Type"
                            required={true}
                        />

                        <div className="mt-4 border-t border-t-surface-5 flex justify-end pt-4 gap-3">
                            <Button btnText="Cancel" isLoading={false} type="close" onClick={() => setShow(false)} />
                            <Button
                                btnText={credentialId ? "Update" : "Submit"}
                                isLoading={credentialId ? updateCredentialResponse.isLoading : createCredentialResponse.isLoading}
                                type="submit"
                                isDisabled={false}
                            />
                        </div>
                    </div>
                } />
            }
        />
    );
};

export default ReswareCredentialsForm;
